import * as React from 'react';
import { Box, IconButton, Tooltip, Typography } from '@material-ui/core';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import AccountTreeOutlinedIcon from '@material-ui/icons/AccountTreeOutlined';
import ViewListIcon from '@material-ui/icons/ViewList';
import ViewModuleIcon from '@material-ui/icons/ViewModule';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import { Taskflow } from '../types';
import { useTaskflowBuilder } from '../taskflowBuilderContext';
import { SectionHeading } from '../../components/cards/SectionHeading';
import { DeleteConfirmationDialog } from '../../components/Dialogs/DeleteConfirmationDialog';

type LibraryViewMode = 'grid' | 'list';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            padding: theme.spacing(3),
        },
        head: {
            marginBottom: theme.spacing(2.5),
        },
        viewToggle: {
            display: 'flex',
            border: `1px solid ${theme.palette.grey[300]}`,
            borderRadius: 8,
            overflow: 'hidden',
            flexShrink: 0,
        },
        viewToggleBtn: {
            borderRadius: 0,
            padding: 7,
            color: theme.palette.text.disabled,
        },
        viewToggleBtnActive: {
            backgroundColor: '#e3f2fd',
            color: theme.palette.primary.main,
            '&:hover': {
                backgroundColor: '#e3f2fd',
            },
        },

        // ── List view ──
        listRow: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1.5),
            padding: theme.spacing(1.5, 1.75),
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 10,
            marginBottom: theme.spacing(1),
            cursor: 'pointer',
            transition: 'box-shadow 0.15s ease, border-color 0.15s ease',
            '&:hover': {
                borderColor: theme.palette.primary.light,
                boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
            },
        },
        listBody: {
            flex: 1,
            minWidth: 0,
        },
        listActions: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(0.5),
            flexShrink: 0,
        },
        deleteIconBtn: {
            color: theme.palette.action.active,
            '&:hover': {
                color: theme.palette.error.main,
                backgroundColor: 'rgba(211, 47, 47, 0.08)',
            },
        },
        listAddRow: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: theme.spacing(1),
            border: `2px dashed ${theme.palette.grey[300]}`,
            borderRadius: 10,
            padding: theme.spacing(1.75),
            marginTop: theme.spacing(1.5),
            color: theme.palette.text.secondary,
            fontSize: 13,
            fontWeight: 700,
            cursor: 'pointer',
            transition: 'border-color 0.15s ease, color 0.15s ease, background-color 0.15s ease',
            '&:hover': {
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                backgroundColor: 'rgba(21, 101, 192, 0.04)',
            },
        },

        // ── Shared card visuals (icon chip, hover lift) ──
        iconChip: {
            width: 40,
            height: 40,
            borderRadius: 10,
            backgroundColor: '#e3f2fd',
            color: theme.palette.primary.main,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
        },
        title: {
            fontSize: 13.5,
            fontWeight: 700,
        },
        meta: {
            fontSize: 11.5,
            color: theme.palette.text.secondary,
            marginTop: 2,
        },
        statusChip: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            fontSize: 10.5,
            fontWeight: 700,
            padding: '3px 10px',
            borderRadius: 20,
            flexShrink: 0,
        },
        statusDot: {
            width: 6,
            height: 6,
            borderRadius: '50%',
        },

        // ── Grid view ──
        grid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: theme.spacing(1.5),
        },
        card: {
            position: 'relative',
            borderRadius: 10,
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            cursor: 'pointer',
            padding: theme.spacing(2, 2, 1.75, 2),
            transition: 'box-shadow 0.15s ease, border-color 0.15s ease, transform 0.15s ease',
            '&:hover': {
                boxShadow: '0 4px 18px rgba(0,0,0,0.10)',
                borderColor: theme.palette.primary.light,
                transform: 'translateY(-1px)',
            },
            '@media (prefers-reduced-motion: reduce)': {
                '&:hover': {
                    transform: 'none',
                },
            },
        },
        cardTop: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: theme.spacing(1.25),
        },
        cardTopActions: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(0.5),
        },
        cardTitle: {
            fontSize: 13.5,
            fontWeight: 700,
            marginTop: theme.spacing(1.5),
            lineHeight: 1.35,
        },
        cardMeta: {
            fontSize: 11.5,
            color: theme.palette.text.secondary,
            marginTop: 4,
        },
        addCard: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: theme.spacing(1),
            border: `2px dashed ${theme.palette.grey[300]}`,
            borderRadius: 10,
            minHeight: 118,
            color: theme.palette.text.secondary,
            fontSize: 12.5,
            fontWeight: 700,
            cursor: 'pointer',
            transition: 'border-color 0.15s ease, color 0.15s ease, background-color 0.15s ease',
            '&:hover': {
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                backgroundColor: 'rgba(21, 101, 192, 0.04)',
            },
        },
    })
);

const STATUS_STYLE: Record<Taskflow['status'], React.CSSProperties> = {
    draft: { backgroundColor: 'rgba(0,0,0,0.06)', color: 'rgba(0,0,0,0.6)' },
    published: { backgroundColor: 'rgba(46, 125, 50, 0.12)', color: '#2e7d32' },
};

const STATUS_DOT_COLOR: Record<Taskflow['status'], string> = {
    draft: 'rgba(0,0,0,0.4)',
    published: '#2e7d32',
};

const describeTaskflowMeta = (taskflow: Taskflow): string => {
    const scope = taskflow.matterCategory.length > 0 ? taskflow.matterCategory.join(', ') : 'All matter categories';
    const taskCount = `${taskflow.taskOrder.length} task${taskflow.taskOrder.length === 1 ? '' : 's'}`;
    return `${scope} · ${taskCount}`;
};

const StatusChip: React.FC<{ status: Taskflow['status']; className: string; dotClassName: string }> = ({ status, className, dotClassName }) => (
    <span className={className} style={STATUS_STYLE[status]}>
        <span className={dotClassName} style={{ backgroundColor: STATUS_DOT_COLOR[status] }} />
        {status === 'draft' ? 'Draft' : 'Published'}
    </span>
);

export const LibraryStep: React.FC = () => {
    const classes = useStyles();
    const { state, dispatch } = useTaskflowBuilder();
    const [viewMode, setViewMode] = React.useState<LibraryViewMode>('grid');
    const [taskflowPendingDelete, setTaskflowPendingDelete] = React.useState<Taskflow | null>(null);

    const openTaskflow = (taskflowId: string) => () => dispatch({ type: 'OPEN_TASKFLOW', taskflowId });
    const startNewTaskflow = () => dispatch({ type: 'START_NEW_TASKFLOW' });

    const requestDeleteTaskflow = (taskflow: Taskflow) => (event: React.MouseEvent) => {
        event.stopPropagation();
        setTaskflowPendingDelete(taskflow);
    };

    const closeDeleteConfirmation = () => {
        setTaskflowPendingDelete(null);
    };

    const confirmDeleteTaskflow = () => {
        if (taskflowPendingDelete) {
            dispatch({ type: 'REMOVE_TASKFLOW', taskflowId: taskflowPendingDelete.id });
        }
        setTaskflowPendingDelete(null);
    };

    return (
        <Box className={classes.root}>
            <Box className={classes.head}>
                <SectionHeading
                    title="Taskflows"
                    description={`${state.library.length} taskflow${state.library.length === 1 ? '' : 's'} in this firm`}
                    color="#1565c0"
                    bgColor="#e3f2fd"
                    icon={AccountTreeOutlinedIcon}
                    headerAction={
                        <Box className={classes.viewToggle}>
                            <Tooltip title="Grid view">
                                <IconButton
                                    size="small"
                                    className={`${classes.viewToggleBtn} ${viewMode === 'grid' ? classes.viewToggleBtnActive : ''}`}
                                    onClick={() => setViewMode('grid')}
                                    aria-label="Grid view"
                                    aria-pressed={viewMode === 'grid'}
                                >
                                    <ViewModuleIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="List view">
                                <IconButton
                                    size="small"
                                    className={`${classes.viewToggleBtn} ${viewMode === 'list' ? classes.viewToggleBtnActive : ''}`}
                                    onClick={() => setViewMode('list')}
                                    aria-label="List view"
                                    aria-pressed={viewMode === 'list'}
                                >
                                    <ViewListIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    }
                />
            </Box>

            {viewMode === 'grid' ? (
                <Box className={classes.grid}>
                    {state.library.map(taskflow => (
                        <Box key={taskflow.id} className={classes.card} onClick={openTaskflow(taskflow.id)}>
                            <Box className={classes.cardTop}>
                                <Box className={classes.iconChip}>
                                    <AccountTreeOutlinedIcon fontSize="small" />
                                </Box>
                                <Box className={classes.cardTopActions}>
                                    <StatusChip status={taskflow.status} className={classes.statusChip} dotClassName={classes.statusDot} />
                                    <IconButton size="small" className={classes.deleteIconBtn} onClick={requestDeleteTaskflow(taskflow)} aria-label="Delete taskflow">
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            </Box>
                            <Typography className={classes.cardTitle}>{taskflow.name || 'Untitled taskflow'}</Typography>
                            <Typography className={classes.cardMeta}>{describeTaskflowMeta(taskflow)}</Typography>
                        </Box>
                    ))}
                    <Box className={classes.addCard} onClick={startNewTaskflow}>
                        <AddIcon fontSize="small" />
                        New Taskflow
                    </Box>
                </Box>
            ) : (
                <>
                    {state.library.map(taskflow => (
                        <Box key={taskflow.id} className={classes.listRow} onClick={openTaskflow(taskflow.id)}>
                            <Box className={classes.iconChip}>
                                <AccountTreeOutlinedIcon fontSize="small" />
                            </Box>
                            <Box className={classes.listBody}>
                                <Typography className={classes.title}>{taskflow.name || 'Untitled taskflow'}</Typography>
                                <Typography className={classes.meta}>{describeTaskflowMeta(taskflow)}</Typography>
                            </Box>
                            <Box className={classes.listActions}>
                                <StatusChip status={taskflow.status} className={classes.statusChip} dotClassName={classes.statusDot} />
                                <IconButton size="small" className={classes.deleteIconBtn} onClick={requestDeleteTaskflow(taskflow)} aria-label="Delete taskflow">
                                    <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        </Box>
                    ))}
                    <Box className={classes.listAddRow} onClick={startNewTaskflow}>
                        <AddIcon fontSize="small" />
                        New Taskflow
                    </Box>
                </>
            )}

            <DeleteConfirmationDialog
                open={!!taskflowPendingDelete}
                onClose={closeDeleteConfirmation}
                onConfirm={confirmDeleteTaskflow}
                itemType="taskflow"
                itemName={taskflowPendingDelete?.name || 'Untitled taskflow'}
            />
        </Box>
    );
};
